import React from 'react';
import { Alert, AlertTitle, Box, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { CheckCircle, Warning, Error as ErrorIcon, HelpOutline } from '@mui/icons-material';
import { ValidationGateResult } from '../../types/experiment.types';

interface ValidationGateAlertProps {
  gate: ValidationGateResult;
  compact?: boolean;
}

const ValidationGateAlert: React.FC<ValidationGateAlertProps> = ({ gate, compact = false }) => {
  const statusConfig: Record<string, { severity: 'success' | 'warning' | 'error' | 'info'; icon: React.ReactNode }> = {
    READY: { severity: 'success', icon: <CheckCircle /> },
    WARNING: { severity: 'warning', icon: <Warning /> },
    ERROR: { severity: 'error', icon: <ErrorIcon /> },
    PENDING: { severity: 'info', icon: <HelpOutline /> },
  };

  const config = statusConfig[gate.status] || statusConfig.PENDING;

  if (compact) {
    return (
      <Alert severity={config.severity} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {config.icon}
        <Typography variant="body2">{gate.message}</Typography>
      </Alert>
    );
  }

  return (
    <Alert severity={config.severity} icon={config.icon}>
      <AlertTitle>{gate.status === 'READY' ? 'Ready for Execution' : 'Validation Issues'}</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {gate.message}
      </Typography>
      
      {gate.blockers && gate.blockers.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Blockers:
          </Typography>
          <List dense sx={{ pl: 2 }}>
            {gate.blockers.map((blocker, idx) => (
              <ListItem key={idx} dense sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <ErrorIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary={blocker}
                  primaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {gate.warnings && gate.warnings.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Warnings:
          </Typography>
          <List dense sx={{ pl: 2 }}>
            {gate.warnings.map((warning, idx) => (
              <ListItem key={idx} dense sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <Warning fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary={warning}
                  primaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Alert>
  );
};

export default ValidationGateAlert;
